'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/styleUtils';
import { VehicleSprite } from '@/modules/captcha/components/CarSprite';
import { PuzzlePrompt, PuzzleScene } from '@/modules/captcha/components/PuzzleFrame';
import { VEHICLE_COLORS, randomInt, shuffle } from '@/modules/captcha/shared/puzzle-utils';
import { useTapChallenge } from '@/modules/captcha/hooks/useTapChallenge';
import { shakeAnimation } from './puzzle.style';
import type { PuzzleProps } from './puzzle.types';

const SCENE_HEIGHT = 170;
const LANE_COUNT = 3;

/** Lane y-offsets, and per-lane duration/delay so the three cars never travel as a block. */
const LANES = [
  { y: 18, duration: '4.2s', delay: '0s' },
  { y: 70, duration: '5.4s', delay: '-2.2s' },
  { y: 124, duration: '4.8s', delay: '-3.4s' },
] as const;

/**
 * Design 2o — tap the named car while all three are driving across.
 *
 * The hardest puzzle in the set, and the only one where the target moves. That is the point: a
 * moving target is the one thing here that a replayed click coordinate cannot solve.
 *
 * ## Invariants:
 * - Motion pauses the instant the challenge is solved, and the tapped car stays put. Why: a car
 *   that keeps sliding after the success state renders reads as the tap not having registered.
 * - Respects `prefers-reduced-motion` by holding the cars still. Why: continuous horizontal
 *   motion is a vestibular trigger, and an accessibility setting must not cost someone the
 *   ability to complete a payment — stationary cars make the puzzle easier, never impossible.
 */
export const ParkingRush = ({ onSolved }: PuzzleProps) => {
  const t = useTranslations('Captcha');
  const [colors] = useState(() => shuffle(VEHICLE_COLORS).slice(0, LANE_COUNT));
  const [answer] = useState(() => randomInt(LANE_COUNT));
  const { wrongIndex, solved, pick } = useTapChallenge(answer, onSolved);

  return (
    <>
      <PuzzlePrompt>
        {t('tapColoredCarPrompt', { color: t(`color_${colors[answer].name}`) })}
      </PuzzlePrompt>
      <PuzzleScene height={SCENE_HEIGHT} data-cy="captcha-parking-rush">
        <div
          className="tw:absolute tw:inset-x-0 tw:top-[54px] tw:h-[2px] tw:bg-[repeating-linear-gradient(90deg,#fff_0_14px,transparent_14px_26px)]"
          aria-hidden="true"
        />
        <div
          className="tw:absolute tw:inset-x-0 tw:top-[110px] tw:h-[2px] tw:bg-[repeating-linear-gradient(90deg,#fff_0_14px,transparent_14px_26px)]"
          aria-hidden="true"
        />
        {colors.map((color, index) => (
          <button
            key={color.name}
            type="button"
            onClick={() => pick(index)}
            className={cn(
              'tw:absolute tw:left-0 tw:h-[22px] tw:w-[48px] tw:cursor-pointer',
              'tw:animate-[pb-captcha-drive_var(--pb-drive-duration)_linear_var(--pb-drive-delay)_infinite]',
              'tw:motion-reduce:animate-none',
              'tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-primary',
              index === wrongIndex && shakeAnimation
            )}
            style={
              {
                top: LANES[index].y,
                '--pb-drive-duration': LANES[index].duration,
                '--pb-drive-delay': LANES[index].delay,
                animationPlayState: solved ? 'paused' : 'running',
              } as React.CSSProperties
            }
            aria-label={t(`color_${color.name}`)}
            data-cy={`captcha-rush-car-${index}`}
          >
            <VehicleSprite kind="car" color={color.hex} width={48} />
          </button>
        ))}
      </PuzzleScene>
    </>
  );
};

ParkingRush.displayName = 'ParkingRush';
